import { useState } from 'react';
import { useStore } from '../../store/useStore';
import { useAuthState } from '../contexts/auth-state-context';
import { createLogger } from '../logger';
import OrganizationService from '../services/organization-service';

const log = createLogger("SwitchOrganizationModal");

type UseSwitchOrganizationProps = {
    setOpen: (value: boolean | ((prevVar: boolean) => boolean)) => void,
    setIsSwitching: (value: boolean | ((prevVar: boolean) => boolean)) => void
}

export const useSwitchOrganization = (props: UseSwitchOrganizationProps) => {
    const {
        setOpen,
        setIsSwitching,
    } = props;

    const { state, setState } = useAuthState();
    const [selectedOrganizationId, setSelectedOrganizationId] = useState(state.organization?.id);

    const { markTagsStale, markGroupsStale, markDeeplinksStale, setOrganization } = useStore((state) => ({
        markTagsStale: state.markTagsStale,
        markGroupsStale: state.markGroupsStale,
        setOrganization: state.setOrganization,
        markDeeplinksStale: state.markDeeplinksStale,
    }))

    const handleSwitchOrganizationClick = async () => {
        if (selectedOrganizationId == null) {
            // TODO : Global exception handling
            // https://app.clickup.com/t/2219993/FMS-1236
            return;
        }

        setIsSwitching(true);
        try {
            const org = await OrganizationService.get(selectedOrganizationId);
            if (org != null) {
                setState((state) => ({
                    ...state, ...{ organization: org }
                }));
                markTagsStale();
                markGroupsStale();
                markDeeplinksStale();
                await setOrganization(org);

                log.info("Organization switched", {
                    organizationId: org.id,
                    organizationName: org.name,
                    previousOrganizationId: state.organization?.id,
                });
            } else {
                log.warn("Organization switch failed: organization not found", {
                    organizationId: selectedOrganizationId,
                });
            }
        } catch (e) {
            log.error("Failed to switch organization", {
                exception: e,
                organizationId: selectedOrganizationId,
            });
        }

        setIsSwitching(false);
        setOpen(false);
    }

    return {
        selectedOrganizationId,
        setSelectedOrganizationId,
        handleSwitchOrganizationClick,
    };
};
